import { useCallback, useState } from "react";
import {
  type AICheckShareBundleImportPreview,
  type AICheckShareBundleRestoreResult,
  importAICheckShareBundleFile,
  previewAICheckShareBundleFile,
} from "@/lib/aicheck/cache-bundle";

export interface PendingShareBundleImport {
  file: File;
  preview: AICheckShareBundleImportPreview;
}

export interface UseShareBundleImportOptions {
  /** Shared status line setter (owned by the workspace). */
  setShareBundleStatus: (status: string | null) => void;
  /**
   * Applies a restored bundle to the workspace state. Kept in the component
   * because it is tightly coupled to workspace-level setters (project data
   * input, freeform restore target, comparison, active tab, project id, run
   * language, cached-result load, ...).
   */
  onApplyImportedBundle: (
    restored: AICheckShareBundleRestoreResult,
  ) => Promise<void>;
}

export interface UseShareBundleImportResult {
  pendingShareBundleImport: PendingShareBundleImport | null;
  isShareBundleImporting: boolean;
  handleImportShareBundle: (file: File) => Promise<void>;
  confirmImportShareBundle: () => Promise<void>;
  cancelImportShareBundle: () => void;
}

/**
 * Share-bundle import dialog lifecycle: preview, confirm (execute), cancel,
 * and the pending/importing state. The actual "apply restored result to the
 * workspace" step is injected via onApplyImportedBundle. Behavior is identical
 * to the previous in-component implementation.
 */
export function useShareBundleImport({
  setShareBundleStatus,
  onApplyImportedBundle,
}: UseShareBundleImportOptions): UseShareBundleImportResult {
  const [pendingShareBundleImport, setPendingShareBundleImport] =
    useState<PendingShareBundleImport | null>(null);
  const [isShareBundleImporting, setIsShareBundleImporting] = useState(false);

  const handleImportShareBundle = useCallback(
    async (file: File) => {
      try {
        setShareBundleStatus("Reading share bundle...");
        const preview = await previewAICheckShareBundleFile(file);
        setPendingShareBundleImport({ file, preview });
        setShareBundleStatus(null);
      } catch (error) {
        setShareBundleStatus(
          error instanceof Error
            ? error.message
            : "Share bundle import failed.",
        );
      }
    },
    [setShareBundleStatus],
  );

  const confirmImportShareBundle = useCallback(async () => {
    if (!pendingShareBundleImport) return;
    setIsShareBundleImporting(true);
    try {
      const restored = await importAICheckShareBundleFile(
        pendingShareBundleImport.file,
        { overwrite: pendingShareBundleImport.preview.overwriteCount > 0 },
      );
      setPendingShareBundleImport(null);
      await onApplyImportedBundle(restored);
      setShareBundleStatus(
        `Imported ${restored.language.toUpperCase()} check result.`,
      );
    } catch (error) {
      setShareBundleStatus(
        error instanceof Error ? error.message : "Share bundle import failed.",
      );
    } finally {
      setIsShareBundleImporting(false);
    }
  }, [onApplyImportedBundle, pendingShareBundleImport, setShareBundleStatus]);

  const cancelImportShareBundle = useCallback(() => {
    if (isShareBundleImporting) return;
    setPendingShareBundleImport(null);
    setShareBundleStatus(null);
  }, [isShareBundleImporting, setShareBundleStatus]);

  return {
    pendingShareBundleImport,
    isShareBundleImporting,
    handleImportShareBundle,
    confirmImportShareBundle,
    cancelImportShareBundle,
  };
}
